/* ---------------------------------------------------------------------------
   Skyline - release scheduling, product page gate.

   Three states, chosen per variant by two metafields:

     no Release At            normal disc, this file does nothing
     Release At + Pre-sell    buyable now, button says Pre-order, note says when
                              it ships
     Release At, no Pre-sell  locked, button disabled, live countdown, opens by
                              itself at zero

   WHAT THIS IS AND IS NOT

   For the locked case this is presentation, not enforcement. It disables the
   button the theme uses, and product-form.js genuinely refuses to submit when
   any of its submit buttons carries `disabled`, so no shopper gets through by
   clicking. It does not stop someone posting a variant id to /cart/add.js by
   hand, and nothing in a theme can: Shopify has no server-side rule available
   on this plan. Where a vendor's street date is the reason, hold the variant
   at zero inventory as well. Then Shopify itself refuses, this file just makes
   the wait legible, and the two agree.

   THE GAP BETWEEN ZERO AND SELLABLE

   The countdown reaching zero does not make a disc sellable. If the release is
   being done by putting stock in at the release minute, there is a window -
   usually seconds, occasionally longer - where the clock says go and Shopify
   still says no. Enabling the button on the clock alone would hand the people
   most eager to buy an error message. So at zero this asks Shopify whether the
   variant is actually purchasable, and keeps asking until it is. The button
   appears a moment late rather than broken, which on a camped drop is the
   trade worth making.

   CLOCK SKEW

   Counted against the server's clock, not the browser's. The release data
   carries the server time it was generated; the offset between that and the
   device is measured once and applied from then on. A shopper whose laptop is
   ten minutes fast would otherwise watch the timer hit zero early, see nothing
   happen, and conclude the site is broken.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (!document.querySelector('product-form')) return;

  var POLL_MS = 3000;        // how often to re-ask Shopify once the clock is up
  var POLL_GIVE_UP_MS = 900000;   // 15 minutes, then stop asking and reload UI
  var TICK_MS = 500;

  var DATA = null;           // { id: {at, presell, avail} }
  var skew = 0;              // serverSeconds - browserSeconds, applied to now()
  var polling = {};          // variant id -> timestamp we started polling
  var handle = null;

  function now() { return Math.floor(Date.now() / 1000) + skew; }

  /* The handle, from the path rather than from any global the theme may or may
     not define. /products/<handle> is stable even on a page reached with a
     ?variant= query or a locale prefix. */
  function productHandle() {
    var m = /\/products\/([^/?#]+)/.exec(window.location.pathname);
    return m ? m[1] : null;
  }

  function load() {
    handle = productHandle();
    if (!handle) return Promise.resolve(null);
    return fetch('/products/' + handle + '?view=release-data', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        if (!json || !json.v) return null;
        /* Measured before anything is rendered, so the first frame of the
           countdown is already corrected. */
        if (json.now) skew = json.now - Math.floor(Date.now() / 1000);
        DATA = {};
        json.v.forEach(function (e) { DATA[String(e.id)] = e; });
        return DATA;
      })
      .catch(function () { return null; });
  }

  /* Ask Shopify directly rather than re-reading our own view, because this is
     the question the cart will actually be asked. product.js is small and
     cached briefly, which is fine at a three second cadence. */
  function refreshAvailability(vid) {
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.variants) return false;
        for (var i = 0; i < p.variants.length; i++) {
          if (String(p.variants[i].id) === String(vid)) {
            var live = !!p.variants[i].available;
            if (DATA[String(vid)]) DATA[String(vid)].avail = live;
            return live;
          }
        }
        return false;
      })
      .catch(function () { return false; });
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function countdownText(seconds) {
    if (seconds < 0) seconds = 0;
    var d = Math.floor(seconds / 86400);
    var h = Math.floor((seconds % 86400) / 3600);
    var m = Math.floor((seconds % 3600) / 60);
    var s = seconds % 60;
    /* Days only while they matter. "02:14:09" reads as a countdown; "0d
       02:14:09" reads as a form field. Abbreviated because this now sits on
       the button next to a label, where "1 day 23:48:03" wraps. */
    if (d > 0) return d + 'd ' + pad(h) + ':' + pad(m) + ':' + pad(s);
    return pad(h) + ':' + pad(m) + ':' + pad(s);
  }

  function releaseLabel(epoch) {
    try {
      return new Date(epoch * 1000).toLocaleString(undefined, {
        weekday: 'long', month: 'long', day: 'numeric',
        hour: 'numeric', minute: '2-digit'
      });
    } catch (e) { return ''; }
  }

  function style() {
    if (document.getElementById('sk-release-style')) return;
    var css = document.createElement('style');
    css.id = 'sk-release-style';
    css.textContent =
      '.sk-release{margin:12px 0;padding:12px 14px;border:1px solid currentColor;' +
      'border-radius:6px;opacity:.95;font-size:14px;line-height:1.45}' +
      '.sk-release-head{font-weight:700;letter-spacing:.04em;text-transform:uppercase;' +
      'font-size:12px;opacity:.8;margin-bottom:4px}' +
      '.sk-release-clock{font-variant-numeric:tabular-nums;font-size:22px;font-weight:700}' +
      '.sk-release-when{opacity:.8;margin-top:4px}' +
      '.sk-release-soon .sk-release-clock{font-size:16px}' +
      /* Tabular figures so the clock does not twitch the button wider and
         narrower once a second. Dimmed because on this theme a disabled
         outline button is nearly indistinguishable from a live one, and a
         locked disc sitting next to a buyable pre-order has to read as the
         one you cannot press. */
      '.sk-locked{font-variant-numeric:tabular-nums;opacity:.55;cursor:not-allowed}' +
      '.sk-express-off{display:none!important}';
    document.head.appendChild(css);
  }

  /* One panel per product-form, inserted after the form rather than inside it,
     so a variant re-render of the form's contents does not take it with it.
     Re-created if it does go missing. */
  function panelFor(form) {
    var el = form.nextElementSibling;
    if (el && el.classList && el.classList.contains('sk-release')) return el;
    el = document.createElement('div');
    el.className = 'sk-release';
    el.setAttribute('role', 'status');
    form.parentNode.insertBefore(el, form.nextSibling);
    return el;
  }

  function clearPanel(form) {
    var el = form.nextElementSibling;
    if (el && el.classList && el.classList.contains('sk-release')) el.remove();
  }

  function buttons(form) {
    return form.querySelectorAll('button[type="submit"]');
  }

  /* Shop Pay, PayPal, Google Pay and friends. These are NOT part of the add to
     cart flow: they hand the variant straight to a hosted checkout, so
     product-form.js never sees the click and a disabled Add button does
     nothing to stop them. Left alone, a locked disc sitting under a live "Buy
     with Shop" button is simply for sale. Hidden rather than disabled because
     these are third-party custom elements that re-render themselves and will
     cheerfully undo an attribute we set on them. */
  function express(pf) {
    return pf.querySelectorAll(
      '.shopify-payment-button, shopify-accelerated-checkout, ' +
      'shopify-accelerated-checkout-cart, .product-form__buy-it-now'
    );
  }

  /* A class with !important rather than an inline style, and this is not
     fussiness. Shopify's accelerated checkout script owns these nodes and
     rewrites their inline style as it initialises: setting el.style.display
     worked for a moment and then the wrapper came back as display:block on its
     own. A stylesheet rule marked !important outranks the inline display the
     script writes, so the hide holds however many times it re-renders. */
  function hideExpress(pf) {
    express(pf).forEach(function (el) { el.classList.add('sk-express-off'); });
  }

  function showExpress(pf) {
    express(pf).forEach(function (el) { el.classList.remove('sk-express-off'); });
  }

  function lock(form, text) {
    buttons(form).forEach(function (b) {
      if (!b.hasAttribute('data-sk-original')) {
        b.setAttribute('data-sk-original', b.innerHTML);
      }
      /* Remember whether the theme had this button enabled before we touched
         it. A sold-out variant arrives already disabled, and restore() must
         leave it that way rather than handing back a button Shopify refuses. */
      if (!b.hasAttribute('disabled')) b.classList.add('sk-was-enabled');
      b.setAttribute('disabled', 'disabled');
      b.setAttribute('aria-disabled', 'true');
      b.classList.add('sk-locked');
      var span = b.querySelector('span:not(.visually-hidden)') || b;
      if (span.textContent !== text) span.textContent = text;
    });
  }

  /* Pre-order is a LIVE button. It has to look like one, which means it must
     not inherit anything a previous lock put on it. The theme normally
     re-renders the form on a variant change and hands us a clean button, but
     it does not always - a partial re-render, or a variant switched some other
     way, and the disabled attribute we set a moment ago is still sitting
     there, making a perfectly buyable pre-order read as unavailable. So this
     actively undoes a lock rather than assuming one never happened.

     Only undoes OUR lock: sk-was-enabled is set in lock() and only when the
     theme had the button enabled to begin with, so a genuinely sold out
     variant stays disabled. */
  function relabel(form, text) {
    buttons(form).forEach(function (b) {
      if (!b.hasAttribute('data-sk-original')) {
        b.setAttribute('data-sk-original', b.innerHTML);
      }
      b.classList.remove('sk-locked');
      if (b.classList.contains('sk-was-enabled')) {
        b.removeAttribute('disabled');
        b.removeAttribute('aria-disabled');
        b.classList.remove('sk-was-enabled');
      }
      var span = b.querySelector('span:not(.visually-hidden)') || b;
      if (span.textContent !== text) span.textContent = text;
    });
  }

  /* Puts back exactly what the theme rendered. Used when the shopper switches
     to a variant with no schedule on it, and after a release opens - the theme
     already knows how to label its own button and whether the variant is sold
     out, and second-guessing it here is how you end up showing Add on a disc
     that just sold. */
  function restore(form) {
    buttons(form).forEach(function (b) {
      var original = b.getAttribute('data-sk-original');
      if (original !== null) {
        b.innerHTML = original;
        b.removeAttribute('data-sk-original');
      }
      b.classList.remove('sk-locked');
      /* Deliberately does not remove `disabled`: the theme sets that on a
         sold-out variant and while a add is in flight, and clearing it here
         would re-enable a button Shopify would refuse. */
      if (b.classList.contains('sk-was-enabled')) {
        b.removeAttribute('disabled');
        b.removeAttribute('aria-disabled');
        b.classList.remove('sk-was-enabled');
      }
    });
  }

  function currentVariant(form) {
    var input = form.querySelector('[name="id"]');
    return input ? String(input.value) : null;
  }

  /* One predicate for "this must not be added right now", shared by the paint
     below and the click interceptor, so the two can never disagree about what
     is locked. Mirrors apply(): a pre-sell is always allowed, a countdown that
     has not finished is not, and a finished countdown is allowed only once
     Shopify agrees the variant is actually purchasable. */
  function blocked(form) {
    var vid = currentVariant(form);
    var entry = (vid && DATA) ? DATA[vid] : null;
    if (!entry || entry.presell) return false;
    if (entry.at - now() > 0) return true;
    return !entry.avail;
  }

  /* Repainting is debounced into a frame rather than done inline. apply() only
     writes when a value actually differs, so a repaint triggered by our own
     DOM change settles on the next pass instead of feeding itself. */
  var queued = false;
  function safeApply() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; apply(); });
  }

  /* THE ACTUAL LOCK, and the reason painting the button is not enough.
     The theme tears down and rebuilds the whole product form on a variant
     change, and for a beat the fresh button is the theme's own: enabled, and
     labelled Add to cart. Someone clicking quickly lands inside that window
     and the add goes through on a disc that is not released yet. Chasing it
     with a faster repaint only narrows the window; it never closes it.

     So the click is refused instead, in the capture phase on document - before
     it reaches the button, before the theme's submit handler, and before it
     can become a form submission. The gap in the paint stops mattering. */
  function intercept(e) {
    var form = null;
    if (e.type === 'submit') {
      form = e.target;
    } else {
      var t = e.target;
      var b = (t && t.closest) ? t.closest('button[type="submit"], input[type="submit"]') : null;
      form = b ? b.closest('form') : null;
    }
    if (!form || !form.closest || !form.closest('product-form')) return;
    if (!blocked(form)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    /* The button just lied to them. Make it tell the truth at once rather
       than leaving a dead control they will click again. */
    safeApply();
  }

  /* Repaints on the frame the theme re-renders rather than up to half a second
     later, so the flash of a live Add to cart is not visible either.

     Anchored on body, not on each product-form, because the theme does not
     merely rewrite the form's contents on a variant change - it replaces the
     element. A per-form observer dies with the node it was watching, which is
     precisely why the first attempt at this still flashed: the replacement was
     not being watched by anything until the next interval.

     Cheap despite the breadth: the callback only schedules, one repaint per
     animation frame at most, and apply() writes nothing when nothing differs,
     so our own edits do not feed it. */
  var watching = false;
  function watchDom() {
    if (watching) return;
    watching = true;
    try {
      new MutationObserver(safeApply).observe(document.body, { childList: true, subtree: true });
    } catch (e) { watching = false; }
  }

  function apply() {
    if (!DATA) return;

    document.querySelectorAll('product-form').forEach(function (pf) {
      var form = pf.querySelector('form');
      if (!form) return;

      var vid = currentVariant(form);
      var entry = vid ? DATA[vid] : null;

      if (!entry) { restore(form); showExpress(pf); clearPanel(form); return; }

      var remaining = entry.at - now();

      if (entry.presell) {
        relabel(form, 'Pre-order');
        showExpress(pf);
        var p = panelFor(form);
        p.className = 'sk-release';
        p.innerHTML =
          '<div class="sk-release-head">Pre-order</div>' +
          '<div>Order now and it ships when it lands, expected <b>' +
          releaseLabel(entry.at) + '</b>.</div>';
        return;
      }

      if (remaining > 0) {
        /* The clock rides on the button itself. It is the thing someone
           waiting on a drop is watching, and it belongs on the control they
           are waiting to press rather than in a panel underneath it. */
        lock(form, 'Coming Soon · ' + countdownText(remaining));
        hideExpress(pf);
        var q = panelFor(form);
        q.className = 'sk-release sk-release-soon';
        q.innerHTML =
          '<div class="sk-release-head">Releases</div>' +
          '<div class="sk-release-when">' + releaseLabel(entry.at) + '</div>';
        return;
      }

      /* Clock is up. Whether it is actually buyable is Shopify's call. */
      if (entry.avail) { restore(form); showExpress(pf); clearPanel(form); return; }

      lock(form, 'Releasing…');
      hideExpress(pf);
      var r = panelFor(form);
      r.className = 'sk-release sk-release-soon';
      r.innerHTML =
        '<div class="sk-release-head">Releasing now</div>' +
        '<div>Stock is going live. This will open in a moment.</div>';

      if (!polling[vid]) {
        polling[vid] = Date.now();
      } else if (Date.now() - polling[vid] > POLL_GIVE_UP_MS) {
        /* Something went wrong with the release rather than being slow.
           Saying so beats spinning forever. */
        r.innerHTML =
          '<div class="sk-release-head">Releasing now</div>' +
          '<div>This should be live. Refresh the page, and if it is still not ' +
          'available it has sold out.</div>';
      }
    });
  }

  var lastPoll = 0;
  function tick() {
    watchDom();
    apply();

    /* Only polls while at least one variant is in the gap, and at most once
       per POLL_MS no matter how many forms are on the page. */
    var due = Object.keys(polling);
    if (due.length && Date.now() - lastPoll > POLL_MS) {
      lastPoll = Date.now();
      due.forEach(function (vid) {
        refreshAvailability(vid).then(function (live) {
          if (live) delete polling[vid];
        });
      });
    }
  }

  function start() {
    style();
    /* Registered before the first paint, so there is no moment where the page
       is interactive and the refusal is not yet in place. */
    document.addEventListener('click', intercept, true);
    document.addEventListener('submit', intercept, true);
    watchDom();
    apply();
    setInterval(tick, TICK_MS);

    /* Three layers, deliberately. The interceptor above is the one that
       actually prevents a sale. The MutationObserver keeps the button honest
       on the frame the theme re-renders. This subscription is a hint from the
       theme when it has one, and the interval below is the backstop for
       anything none of them saw. Only the first is load-bearing. */
    try {
      if (window.webvista && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
        webvista.subscribe(PUB_SUB_EVENTS.variantChange, safeApply);
      }
    } catch (e) {}
  }

  load().then(function (data) { if (data) start(); });
})();
